import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCheck, FiLogOut } from 'react-icons/fi';
import LogoutModal from '../components/LogoutModal';

const PageContainer = styled(motion.div)`
  min-height: 100vh;
  background: white;
  padding: 20px;
  font-family: 'Poppins', sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
  position: relative;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #333;
  padding: 8px;
`;

const HeaderTitle = styled.span`
  font-size: 16px;
  color: #333;
  font-weight: 500;
`;

const Logo = styled.div`
  width: 45px;
  height: 45px;
  background-color: #ff0000;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 10px;
  text-align: center;
  line-height: 1.2;
`;

const EnquiryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Toast = styled(motion.div)`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: #4CAF50;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  pointer-events: none;
  
  svg {
    font-size: 18px;
  }
`;

const EnquiryCard = styled(motion.div)`
  background: rgb(248, 247, 247);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 24px;
  border: 1px solid rgba(207, 49, 49, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    background: white;
    border-color: rgba(207, 49, 49, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 12px;
    gap: 8px;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  @media (min-width: 481px) {
    flex-direction: row;
    gap: 16px;
    justify-content: flex-start;
  }
`;

interface CardRowProps {
  isFirstRow?: boolean;
}

interface InfoContainerProps {
  isFirstRow?: boolean;
}

const CardRow = styled.div<CardRowProps>`
  display: flex;
  gap: 12px;
  width: 100%;
  align-items: center;
  flex-wrap: wrap;

  @media (min-width: 481px) {
    width: auto;
    flex: ${props => props.isFirstRow ? '0 0 auto' : '1'};
    min-width: ${props => props.isFirstRow ? '140px' : '0'};
    margin-right: ${props => props.isFirstRow ? '8px' : '0'};
  }

  @media (max-width: 480px) {
    flex-direction: row;
    justify-content: ${props => props.isFirstRow ? 'center' : 'space-between'};
    gap: ${props => props.isFirstRow ? '12px' : '8px'};
  }
`;

interface EnquiryRowProps {
  flex?: string;
  minWidth?: string;
  isTablet?: boolean;
  isFirstRow?: boolean;
}

const EnquiryRow = styled.div<EnquiryRowProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  
  @media (min-width: 481px) {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: 480px) {
    width: ${props => props.isFirstRow ? 'auto' : 'calc(50% - 4px)'};
    justify-content: ${props => props.isFirstRow ? 'center' : 'flex-start'};
  }
`;

const InfoContainer = styled.div<InfoContainerProps>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  min-width: 0;

  @media (max-width: 480px) {
    width: ${props => props.isFirstRow ? 'auto' : '100%'};
    align-items: ${props => props.isFirstRow ? 'center' : 'flex-start'};
  }
`;

const MainText = styled.div`
  font-size: 11px;
  color: rgb(250, 154, 154);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: rgb(250, 154, 154);
    flex-shrink: 0;
    width: 18px;
    height: 18px;
  }
`;

const SubText = styled.div`
  font-size: 14px;
  color: #333;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-left: 26px;
  min-width: 0;
`;

const ContactInfoContainer = styled(InfoContainer)`
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    ${SubText} {
      color: #ff0000;
    }
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #333;
  padding: 8px;
`;

const mockEnquiries = Array.from({ length: 9 }, (_, i) => ({
  id: i + 1,
  name: 'Lorem',
  contact: '+91 9876543210',
  date: new Date(2024, 1, i + 1).toLocaleDateString(),
  salesAdvisor: 'Sarah Johnson'
}));

const ViewEnquiries = () => {
  const navigate = useNavigate();
  const [showToast, setShowToast] = useState(false);
  const [copiedNumber, setCopiedNumber] = useState('');
  const [toastKey, setToastKey] = useState(0);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showToast) {
      timer = setTimeout(() => {
        setShowToast(false);
      }, 2000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showToast]);

  const handleCopyContact = async (contact: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(contact);
      setCopiedNumber(contact);
      setShowToast(false);
      setTimeout(() => {
        setToastKey(prev => prev + 1);
        setShowToast(true);
      }, 10);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <PageContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/home')}>
            <FiArrowLeft />
          </BackButton>
          <HeaderTitle>View Enquiry</HeaderTitle>
        </HeaderLeft>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Logo>
            STYLE<br /><span style={{fontWeight: '900'}}>BOX</span>
          </Logo>
          <ActionButton onClick={() => setShowLogoutConfirm(true)}>
            <FiLogOut size={16} />
          </ActionButton>
        </div>
      </Header>

      <EnquiryList>
        {mockEnquiries.map((enquiry) => (
          <EnquiryCard
            key={enquiry.id}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            <CardContent>
              <CardRow isFirstRow>
                <EnquiryRow isFirstRow>
                  <InfoContainer isFirstRow>
                    <MainText>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                        <path d="M3 8h18" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      Enquiry ID
                    </MainText>
                    <SubText>#{enquiry.id}</SubText>
                  </InfoContainer>
                </EnquiryRow>
              </CardRow>

              <CardRow>
                <EnquiryRow>
                  <InfoContainer>
                    <MainText>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 3h18v18H3V3z M3 9h18 M9 3v18" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      Date
                    </MainText>
                    <SubText>{enquiry.date}</SubText>
                  </InfoContainer>
                </EnquiryRow>

                <EnquiryRow>
                  <InfoContainer>
                    <MainText>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2"/>
                        <path d="M20 21c0-4.418-3.582-8-8-8s-8 3.582-8 8" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      Sales Advisor
                    </MainText>
                    <SubText>{enquiry.salesAdvisor}</SubText>
                  </InfoContainer>
                </EnquiryRow>
              </CardRow>

              <CardRow>
                <EnquiryRow>
                  <InfoContainer>
                    <MainText>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2"/>
                        <path d="M20 21c0-4.418-3.582-8-8-8s-8 3.582-8 8" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      Client Name
                    </MainText>
                    <SubText>{enquiry.name}</SubText>
                  </InfoContainer>
                </EnquiryRow>

                <EnquiryRow>
                  <InfoContainer>
                    <MainText>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Client Contact
                    </MainText>
                    <SubText>{enquiry.contact}</SubText>
                  </InfoContainer>
                </EnquiryRow>
              </CardRow>
            </CardContent>
          </EnquiryCard>
        ))}
      </EnquiryList>

      {showToast && (
        <Toast
          key={toastKey}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.2,
            ease: "easeOut"
          }}
        >
          <FiCheck />
          Number copied: {copiedNumber}
        </Toast>
      )}

      {showLogoutConfirm && (
        <LogoutModal onClose={() => setShowLogoutConfirm(false)} />
      )}
    </PageContainer>
  );
};

export default ViewEnquiries; 